@use "../../sass/global/global" as *;

/** Logic behind the "z-index" changes:
<ux-iws-search-bar class="ux-iws-search-bar--with-autosuggest"> // z-index: 800; puts the overlay in front of non-sticky header when autosuggest is shown; should be higher as 799 (z-index set for <ux-iws-header>)
    <div class="ux-iws-search-bar__inner">
        <div class="ux-iws-search-bar__controls">...</div> // z-index: 2; should be higher as for ".ux-iws-search-bar__autosuggest" in order GPT tooltip tip is displayed in front of the autosuggestions
        <div class="ux-iws-search-bar__autosuggest">...</div> z-index: 1;
        <div class="ux-iws-search-bar__content">...</div> z-index: 0; should be the lowest among the siblings in order the search tabs and search results appear behind an overlay
        <div class="ux-iws-search-bar__overlay"></div>
    </div>
</ux-iws-search-bar> **/

$z-index-searchbar-with-autosuggest: 800; // puts the overlay in front of non-sticky header when autosuggest is shown; should be higher as 799 (z-index set for <ux-iws-header>)
$z-index-controls: 2; // puts input and all search related buttons above the overlay
$z-index-autosuggest: 1; // puts gpt tooltip tip above the autosuggestions block
$z-index-content: 0; // puts search tabs and search results behind the searchbar with autosuggest

.ux-iws-search-bar {
    .ux-input {
        text-align: start;
        background-color: $color-white;
        transition: border-color $animationSpeed-fast $animationEasing-ease;
        padding-right: $dim-40;
    }

    ux-button {
        flex: 0 0 auto;
    }

    &__inner {
        width: 100%;
        background-color: $color-white;
        box-shadow: 0 7px 16px rgba(19, 19, 19, 0);
        transition: box-shadow $animationSpeed-normal $animationEasing-ease;

        .is-sticky & {
            position: fixed;
            z-index: 999;
            left: 0;
            top: -10px;
            box-shadow: 0 7px 16px rgba(19, 19, 19, 0.08);
        }
    }

    &__controls,
    &__autosuggest {
        width: 100%;
        max-width: 850px;
        display: flex;
        flex-flow: row wrap;
        margin: 0 auto;
        padding: $dim-30 $dim-15 $dim-50;
        background: $color-white;

        @media only screen and (min-width: 1440px) {
            padding: 50px 15px;
        }
    }

    &__autosuggest {
        position: relative;
        display: block;
        top: -40px;
        padding: 0 15px;
    }

    .ux-iws-search-bar__overlay {
        opacity: 0;
        transition: opacity 0.3s;
    }

    &__content {
        padding-bottom: 10px;
        background: $color-white;

        .ux-tab-container__tab-header {
            padding-left: 0;
            padding-right: 0;
        }

        .ux-tab-container__tab-body {
            padding: 0;
        }
    }

    &__content.is-sticky {
        @media (max-width: 567px) {
            display: block;
            transform: translateY(100%);
            transition-delay: 0.1s;
        }
    }

    &__spacer {
        display: inline-block;
        flex: 0 0 $dim-10;
    }

    &__field {
        position: relative;
        flex: 1 1 auto;
        text-align: start;
    }

    &__reset-btn {
        appearance: none;
        background: transparent none;
        border: 0 none transparent;
        outline: none;
        position: absolute;
        right: 0;
        top: 0;
        width: $dim-40;
        height: 100%;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        pointer-events: none;
        opacity: 0;
        transition: opacity $animationSpeed-fast ease-out;

        &--is-active {
            opacity: 1;
            pointer-events: auto;
            cursor: pointer;
        }

        ux-icon {
            font-size: 14px !important;
        }
    }

    &--no-padding {
        padding: 0;
        margin: 0;

        .ux-iws-search-bar__inner {
            padding-top: 0;
        }

        .ux-iws-search-bar__controls {
            padding: 0;
        }

        .ux-iws-search-bar__content {
            padding-bottom: 0;
        }
    }

    &__buttons {
        --ux-button-primary-text-color: var(
            --ux-button-primary-hover-text-color
        );
        --ux-button-primary-bg-color: var(--ux-button-primary-hover-bg-color);

        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        justify-content: center;
        gap: 10px;
        margin-left: 10px;

        @media (max-width: 533px) {
            width: 100%;
            margin-top: 10px;
        }

        .ux-icon[name="square-info"] {
            background-color: #fff;
        }
    }

    &__hidden {
        display: none;
    }

    &__blocker {
        position: relative;
        width: 100%;
        height: 0;
    }

    &--compact {
        .ux-iws-search-bar__spacer {
            display: none;
        }
    }
}

.ux-iws-search-bar__search-button {
    &__label {
        display: inline-block;
    }

    ux-icon {
        display: none;
        font-size: 16px;
    }

    @media only screen and (max-width: 767px) {
        --ux-button-min-width: 44px;
        --ux-button-padding-horizontal: 0;

        &__label {
            display: none;
        }

        ux-icon {
            display: inline-block;
        }
    }
}

[dir="rtl"] {
    .ux-iws-search-bar {
        .ux-input {
            padding-right: 20px;
            padding-left: $dim-40;
        }

        &__reset-btn {
            right: auto;
            left: 0;
        }
    }
}

.website-search-form {
    margin-bottom: 5px;

    @media only screen and (min-width: 768px) {
        margin-bottom: 0;
    }
}

.ux-iws-search-bar--with-autosuggest {
    z-index: $z-index-searchbar-with-autosuggest;
    position: relative;

    .ux-iws-search-bar {
        &__controls {
            z-index: $z-index-controls;
            position: relative;
            background: transparent;
        }

        &__autosuggest {
            z-index: $z-index-autosuggest;
        }

        &__content {
            z-index: $z-index-content;
            position: relative;
        }

        &__overlay {
            opacity: 1;
            background-color: rgba(0, 0, 0, 0.2);
            position: absolute;
            width: 200vw;
            height: 1000vh;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            pointer-events: none;
        }
    }

    &.ux-iws-search-bar--no-padding {
        .ux-iws-search-bar__autosuggest {
            padding: 0;
            top: 10px;
        }
    }
}

.ux-iws-search-bar--icon-only {
    ux-icon {
        display: inline-block;
    }
}
